import { CreditCard } from "lucide-react"

import { OrderStatus, type SubOrder } from "@/api/api-client"
import { Button } from "@/components/ui/button"

import type { PaymentRequiredLabels } from "./buyer-order-types"
import { getShippingQuoteSubOrders, isPaymentAttemptProcessing } from "./buyer-order-utils"

interface PaymentRequiredStripProps {
  subOrders?: SubOrder[]
  labels: PaymentRequiredLabels
  isCheckingOut: boolean
  checkingOutSubOrderId: number | null
  awaitingPaymentSubOrderIds?: ReadonlySet<number>
  onPayNow: (subOrderId: number) => void
}

export function PaymentRequiredStrip({
  subOrders,
  labels,
  isCheckingOut,
  checkingOutSubOrderId,
  awaitingPaymentSubOrderIds,
  onPayNow,
}: PaymentRequiredStripProps) {
  const quotedSubOrders = getShippingQuoteSubOrders(subOrders)
  if (quotedSubOrders.length === 0) return null

  return (
    <div className="border-t border-amber-500/30 bg-amber-500/5">
      {quotedSubOrders.map((subOrder) => {
        if (subOrder.id == null) return null

        const isLoadingThis = isCheckingOut && checkingOutSubOrderId === subOrder.id
        const isAwaitingPaymentSettlement =
          awaitingPaymentSubOrderIds?.has(subOrder.id) || isPaymentAttemptProcessing(subOrder)
        const isDisabled =
          isLoadingThis ||
          isAwaitingPaymentSettlement ||
          subOrder.status !== OrderStatus.ShippingQuoted
        const buttonLabel = isLoadingThis
          ? "..."
          : isAwaitingPaymentSettlement
            ? labels.paymentProcessing || "Processing payment"
            : labels.payAmount.replace("{amount}", (subOrder.totalAmount ?? 0).toFixed(2))

        return (
          <div
            key={subOrder.id}
            className="flex flex-col gap-4 border-l-4 border-amber-500 px-6 py-4 sm:flex-row sm:items-center"
          >
            <div className="flex flex-1 items-center gap-3">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
              </span>
              <p className="text-xs font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">
                {labels.paymentRequired.replace("{ref}", String(subOrder.id))}
              </p>
            </div>
            <Button
              onClick={() => onPayNow(subOrder.id!)}
              disabled={isDisabled}
              size="sm"
              className="shrink-0 border-0 bg-amber-500 px-6 text-xs font-black uppercase tracking-widest text-white hover:bg-amber-600"
            >
              <CreditCard className="mr-2 h-3.5 w-3.5" />
              {buttonLabel}
            </Button>
          </div>
        )
      })}
    </div>
  )
}
